import { Archive, ChevronDown, ChevronRight, ClipboardList, Copy, FileCheck2, Layers, MoreHorizontal, PenLine, Plus, Search, Send, Trash2, Users } from 'lucide-react';
import { formatSurveyNumber } from '@survey/core';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import type { SurveyStatus, SurveySummary } from '../api/types';
import { Topbar } from '../components/Layout';
import { confirmAction, Empty, Menu, PageLoader, StatusPill, useToast } from '../components/ui';
import { formatNumber, relativeTime } from '../format';
import { useCollapsed, useStoredState } from '../hooks/useStoredState';
import { useI18n, type MessageKey } from '../i18n';

const FILTERS: { key: '' | SurveyStatus; label: MessageKey }[] = [
  { key: '', label: 'surveys.filter_all' },
  { key: 'draft', label: 'surveys.filter_drafts' },
  { key: 'published', label: 'surveys.filter_live' },
  { key: 'closed', label: 'surveys.filter_closed' },
  { key: 'archived', label: 'surveys.filter_archived' },
];
const RESPONSE_REFRESH_MS = 5_000;
const GROUP_ORDER: SurveyStatus[] = ['published', 'draft', 'closed', 'archived'];

export function SurveysPage() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as '' | SurveyStatus;
  const [query, setQuery] = useState(params.get('q') ?? '');
  const [surveys, setSurveys] = useState<SurveySummary[] | null>(null);
  const [canCreate, setCanCreate] = useState(false);
  const [error, setError] = useState('');
  const toast = useToast();
  const { t } = useI18n();
  const [summaryOpen, setSummaryOpen] = useStoredState('survey.list.summary_open', true);
  const [grouped, setGrouped] = useStoredState('survey.list.grouped', false);
  const [collapsed, toggle] = useCollapsed('survey.list.collapsed');

  const load = useMemo(
    () => async (silent = false) => {
      try {
        const res = await api.listSurveys({ q: params.get('q') ?? undefined, status: status || undefined });
        setSurveys(res.surveys);
        setCanCreate(res.canCreate);
        setError('');
      } catch (e) {
        if (!silent) setError(e instanceof ApiError ? e.message : t('surveys.load_failed'));
      }
    },
    [params, status, t],
  );

  useEffect(() => {
    void load();
  }, [load]);

  // Keep shared response counts fresh for every role that can see the survey list.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') void load(true);
    };
    const timer = window.setInterval(refresh, RESPONSE_REFRESH_MS);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  // Debounced search into the URL so results are shareable and survive reloads.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (query) next.set('q', query);
      else next.delete('q');
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, params, setParams]);

  async function act(fn: () => Promise<unknown>, done: string) {
    try {
      await fn();
      toast(done);
      await load();
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('common.action_failed'), 'error');
    }
  }

  const totals = useMemo(() => {
    const s = surveys ?? [];
    return {
      count: s.length,
      live: s.filter((x) => x.status === 'published').length,
      drafts: s.filter((x) => x.status === 'draft').length,
      responses: s.reduce((n, x) => n + x.responseCount, 0),
    };
  }, [surveys]);

  return (
    <>
      <Topbar title={t('surveys.title')}>
        {canCreate && (
          <Link to="/surveys/new" className="btn primary">
            <Plus /> {t('surveys.new')}
          </Link>
        )}
      </Topbar>

      <div className={`card mb-16 collapsible ${summaryOpen ? '' : 'closed'}`}>
        <button type="button" className="collapse-toggle btn ghost sm icon" aria-expanded={summaryOpen} onClick={() => setSummaryOpen(!summaryOpen)} aria-label={t('surveys.toggle_summary')} title={t('surveys.toggle_summary')}>
          <ChevronDown className={`chev ${summaryOpen ? '' : 'closed'}`} />
        </button>
        {summaryOpen ? (
          <div className="kpis">
            <Kpi icon={<ClipboardList />} label={t('surveys.kpi_total')} value={totals.count} />
            <Kpi icon={<Send />} label={t('surveys.kpi_live')} value={totals.live} />
            <Kpi icon={<PenLine />} label={t('surveys.kpi_drafts')} value={totals.drafts} />
            <Kpi icon={<FileCheck2 />} label={t('surveys.kpi_responses')} value={totals.responses} />
          </div>
        ) : (
          <div className="kpi-strip small">
            <span><ClipboardList /> {t('surveys.kpi_total')} <strong>{formatNumber(totals.count)}</strong></span>
            <span><Send /> {t('surveys.kpi_live')} <strong>{formatNumber(totals.live)}</strong></span>
            <span><PenLine /> {t('surveys.kpi_drafts')} <strong>{formatNumber(totals.drafts)}</strong></span>
            <span><FileCheck2 /> {t('surveys.kpi_responses')} <strong>{formatNumber(totals.responses)}</strong></span>
          </div>
        )}
      </div>

      <div className="row gap-12 wrap mb-16">
        <div className="tabs" role="tablist">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              role="tab"
              aria-selected={status === f.key}
              className={`tab ${status === f.key ? 'active' : ''}`}
              onClick={() => {
                const next = new URLSearchParams(params);
                if (f.key) next.set('status', f.key);
                else next.delete('status');
                setParams(next);
              }}
            >
              {t(f.label)}
            </button>
          ))}
        </div>
        <div className="spacer grow" />
        {!status && (
          <button type="button" className={`btn sm ${grouped ? 'on' : ''}`} aria-pressed={grouped} onClick={() => setGrouped(!grouped)}>
            <Layers /> {t('surveys.group_by_status')}
          </button>
        )}
        <div className="search" style={{ width: 260, maxWidth: '100%' }}>
          <Search />
          <input className="input" placeholder={t('surveys.search_placeholder')} value={query} onChange={(e) => setQuery(e.target.value)} aria-label={t('surveys.search_label')} />
        </div>
      </div>

      {error && <div className="banner error mb-16">{error}</div>}

      <div className="card" hidden={!!surveys?.length && grouped && !status}>
        {surveys === null ? (
          <PageLoader />
        ) : surveys.length === 0 ? (
          <Empty icon={<ClipboardList />} title={params.get('q') || status ? t('surveys.no_matches') : t('surveys.none_yet')}>
            {canCreate && !params.get('q') && !status ? (
              <Link to="/surveys/new" className="btn primary mt-8">
                <Plus /> {t('surveys.create_first')}
              </Link>
            ) : (
              <p className="small">{t('surveys.try_other_filter')}</p>
            )}
          </Empty>
        ) : grouped && !status ? null : (
          <SurveyTable surveys={surveys} canCreate={canCreate} act={act} />
        )}
      </div>
      {surveys && surveys.length > 0 && grouped && !status && (
        <div className="stack gap-16">
          {GROUP_ORDER.map((st) => {
            const list = surveys.filter((x) => x.status === st);
            if (!list.length) return null;
            const open = !collapsed.has(st);
            return (
              <div key={st} className="card">
                <button type="button" className="collapse-head" aria-expanded={open} onClick={() => toggle(st)} aria-label={t('surveys.toggle_group', { status: t(`status.${st}`) })}>
                  <ChevronRight className={`chev ${open ? 'open' : ''}`} />
                  <StatusPill status={st} />
                  <span className="muted small">{formatNumber(list.length)}</span>
                </button>
                {open && <SurveyTable surveys={list} canCreate={canCreate} act={act} />}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

function SurveyTable({ surveys, canCreate, act }: { surveys: SurveySummary[]; canCreate: boolean; act: (fn: () => Promise<unknown>, done: string) => Promise<void> }) {
  const nav = useNavigate();
  const { t } = useI18n();
  return (
    <div className="table-wrap" style={{ paddingTop: 16 }}>
      <table className="table">
        <thead>
          <tr>
            <th>{t('surveys.col_no')}</th>
            <th>{t('surveys.col_questionnaire')}</th>
            <th>{t('surveys.col_code')}</th>
            <th>{t('surveys.col_status')}</th>
            <th>{t('surveys.col_owner')}</th>
            <th className="num">{t('surveys.col_responses')}</th>
            <th>{t('surveys.col_updated')}</th>
            <th aria-label={t('surveys.col_actions')} />
          </tr>
        </thead>
        <tbody>
          {surveys.map((s) => {
            const can = (p: string) => s.permissions.includes(p as never);
            const open = can('survey.edit') ? `/surveys/${s.id}` : `/surveys/${s.id}/responses`;
            return (
              <tr key={s.id} className="clickable" onClick={() => nav(open)}>
                <td className="number-tag" style={{ width: 80 }}>
                  {formatSurveyNumber(s.number)}
                </td>
                <td>
                  <div className="title-cell">{s.title || t('common.untitled_questionnaire')}</div>
                  {s.relation !== 'owner' && s.relation !== 'admin' && (
                    <div className="muted row gap-4">
                      <Users size={12} /> {t('surveys.shared_with_you', { relation: s.relation ? t(`relation.${s.relation}`) : '' })}
                    </div>
                  )}
                </td>
                <td>
                  <span className="code">{s.code}</span>
                </td>
                <td>
                  <StatusPill status={s.status} />
                </td>
                <td className="muted">{s.owner.name || s.owner.email}</td>
                <td className="num">{formatNumber(s.responseCount)}</td>
                <td className="muted">{relativeTime(s.updatedAt)}</td>
                <td onClick={(e) => e.stopPropagation()} style={{ width: 40 }}>
                  <Menu
                    trigger={(toggle) => (
                      <button className="btn ghost sm icon" onClick={toggle} aria-label={t('surveys.actions_for', { title: s.title })}>
                        <MoreHorizontal />
                      </button>
                    )}
                  >
                    {(close) => (
                      <>
                        {can('survey.edit') && (
                          <button onClick={() => nav(`/surveys/${s.id}`)}>
                            <PenLine /> {t('surveys.edit')}
                          </button>
                        )}
                        {can('responses.view') && (
                          <button onClick={() => nav(`/surveys/${s.id}/responses`)}>
                            <FileCheck2 /> {t('surveys.responses')}
                          </button>
                        )}
                        {canCreate && (
                          <button
                            onClick={() => {
                              close();
                              void act(() => api.duplicate(s.id), t('surveys.duplicated'));
                            }}
                          >
                            <Copy /> {t('surveys.duplicate')}
                          </button>
                        )}
                        {can('survey.edit') && s.status !== 'archived' && (
                          <button
                            onClick={() => {
                              close();
                              void act(() => api.archive(s.id), t('surveys.archived'));
                            }}
                          >
                            <Archive /> {t('surveys.archive')}
                          </button>
                        )}
                        {can('survey.edit') && s.status === 'archived' && (
                          <button
                            onClick={() => {
                              close();
                              void act(() => api.restore(s.id), t('surveys.restored'));
                            }}
                          >
                            <Archive /> {t('surveys.restore')}
                          </button>
                        )}
                        {can('survey.delete') && (
                          <button
                            className="danger"
                            onClick={() => {
                              close();
                              const msg =
                                s.responseCount > 0
                                  ? t('surveys.confirm_delete_with_responses', { title: s.title, count: s.responseCount })
                                  : t('surveys.confirm_delete', { title: s.title });
                              if (confirmAction(msg)) void act(() => api.deleteSurvey(s.id), t('surveys.deleted'));
                            }}
                          >
                            <Trash2 /> {t('surveys.delete')}
                          </button>
                        )}
                      </>
                    )}
                  </Menu>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Kpi({ icon, label, value }: { icon: React.ReactNode; label: string; value: number }) {
  return (
    <div className="kpi">
      <div className="tile">{icon}</div>
      <div className="body">
        <div className="label">{label}</div>
        <div className="value">{formatNumber(value)}</div>
      </div>
    </div>
  );
}
